// src/pages/b2b/ProductDetailPageB2B.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  CircularProgress,
  Divider,
  Typography,
  Breadcrumbs,
  Link as MLink,
} from "@mui/material";

import { ProductsAPI } from "../../../../../api/Product";
import ProductDetailB2BTop from "./ProductDetailB2BTop";
import ProductDetailB2BVariants from "./ProductDetailVariants";
import ProductDetailB2BInfo from "./ProductDetailB2BInfo";
import ProductSuggestionSection from "./ProductSuggestionSection";
import NotFoundPage from "../../../NotFoundPage";
import { useAuth } from "../../../Auth/AuthContext";
import { getLangFromCookie } from "../../../../../Utils/LangCookie";

// ✅ SAME AS ProductInfoPageB2B
import { resolveAssetUrl } from "../../../../../lib/http";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const FALLBACK_IMG = "/img/empty.jpg";

function isValidImg(val) {
  return (
    !!val &&
    typeof val === "string" &&
    val.trim() !== "" &&
    val !== "null" &&
    val !== "undefined"
  );
}

function safeJsonArray(v) {
  try {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    const parsed = JSON.parse(String(v));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function normalizeItem(x) {
  const rawImages = Array.isArray(x?.images) ? x.images : [];

  const fromArray = rawImages
    .map((im) =>
      typeof im === "string"
        ? im
        : im?.url || im?.image || im?.imageUrl || im?.path,
    )
    .filter(isValidImg);

  const fromMetaJson = safeJsonArray(
    x?.image_urls_json || x?.imageUrlsJson || x?.meta?.image_urls_json,
  )
    .map((im) =>
      typeof im === "string"
        ? im
        : im?.url || im?.image || im?.imageUrl || im?.path,
    )
    .filter(isValidImg);

  const singles = [
    x?.image,
    x?.imageUrl,
    x?.mainImage,
    x?.cover,
    x?.main?.image,
    x?.main?.imageUrl,
    x?.main?.["主圖"],
  ].filter(isValidImg);

  const allImages = Array.from(
    new Set(
      [...fromArray, ...fromMetaJson, ...singles]
        .map((s) => resolveAssetUrl(String(s)))
        .filter(Boolean),
    ),
  );

  const primaryImage = allImages[0] || FALLBACK_IMG;

  const productName =
    x?.main?.["產品名稱"] ||
    x?.main?.name ||
    x?.productName ||
    x?.name ||
    x?.productID ||
    x?.productId ||
    x?.id;

  const normalizedId =
    x?.productID ??
    x?.productId ??
    x?.id ??
    x?.product_id ??
    x?.main?.["編號"] ??
    x?.main?.id;

  return {
    ...x,
    id: normalizedId,
    productName,
    images: allImages.length ? allImages : [FALLBACK_IMG],
    image: primaryImage,

    // keep favourite if backend returned it
    favourite:
      typeof x?.favourite === "number" ? x.favourite : (x?.favourite ?? 0),
  };
}

/* -------------------------------------------------------------------------- */
/* API compatibility                                                           */
/* -------------------------------------------------------------------------- */

async function apiGetVariantOptions(parentID, { lang, userID }) {
  if (typeof ProductsAPI?.variantOptions === "function") {
    return ProductsAPI.variantOptions(parentID, { lang, userID });
  }
  if (typeof ProductsAPI?.variantsOpt === "function") {
    return ProductsAPI.variantsOpt(parentID, { lang, userID });
  }
  return {};
}

async function apiGetVariantsByParent(parentID, params) {
  if (typeof ProductsAPI?.byParent === "function") {
    return ProductsAPI.byParent(parentID, params);
  }
  if (typeof ProductsAPI?.parent === "function") {
    return ProductsAPI.parent(parentID, params);
  }
  if (typeof ProductsAPI?.detailWithVariants === "function") {
    return ProductsAPI.detailWithVariants(parentID, params);
  }
  return [];
}

/* -------------------------------------------------------------------------- */

export default function ProductDetailPageB2B() {
  const { id } = useParams(); // parentID
  const { user } = useAuth();
  const isLoggedIn = !!user;

  // ✅ match what we used elsewhere: userID is just a string
  const uid = useMemo(() => {
    const u = user || {};
    return String(
      u.sehoUserID ||
        u.userID ||
        u.userId ||
        u.uid ||
        u.memberID ||
        u.memberId ||
        u.wpUserId ||
        u.username ||
        u.email ||
        u.user?.sehoUserID ||
        u.user?.userID ||
        u.user?.userId ||
        u.user?.uid ||
        u.user?.memberID ||
        u.user?.memberId ||
        u.user?.wpUserId ||
        u.user?.username ||
        u.user?.email ||
        u.id ||
        u.ID ||
        u.user?.id ||
        u.user?.ID ||
        "",
    ).trim();
  }, [user]);

  const location = useLocation();
  const navigate = useNavigate();

  const variantFromQuery = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const v = sp.get("variant");
      return v ? String(v) : "";
    } catch {
      return "";
    }
  }, [location.search]);

  const appliedVariantKeyRef = useRef("");

  const receivedCrumbs = location.state?.fromBreadcrumbs || [];
  const fromCategoryHref = location.state?.fromCategoryHref || "";

  const [product, setProduct] = useState(null);

  const [variants, setVariants] = useState([]);
  const [allVariants, setAllVariants] = useState([]);

  const [variantOptions, setVariantOptions] = useState({});

  const [selectedVariantId, setSelectedVariantId] = useState(null);

  const [selectedA, setSelectedA] = useState("");
  const [selectedB, setSelectedB] = useState("");
  const [selectedC, setSelectedC] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [lang, setLang] = useState(() => getLangFromCookie() || "ZH");

  useEffect(() => {
    const syncLang = () => setLang(getLangFromCookie() || "ZH");
    syncLang();

    window.addEventListener("app:languageChanged", syncLang);
    return () => window.removeEventListener("app:languageChanged", syncLang);
  }, []);

  useEffect(() => {
    setSelectedA("");
    setSelectedB("");
    setSelectedC("");

    setVariantOptions({});
    setVariants([]);
    setAllVariants([]);

    setProduct(null);
    setSelectedVariantId(null);

    appliedVariantKeyRef.current = "";
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    async function loadOptions() {
      try {
        const opts = await apiGetVariantOptions(id, { lang, userID: uid });
        if (!cancelled) setVariantOptions(opts || {});
      } catch (e) {
        console.error("Failed to load variant options", e);
        if (!cancelled) setVariantOptions({});
      }
    }

    if (id) loadOptions();
    return () => (cancelled = true);
  }, [id, lang, uid]);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      if (!id) return;

      setLoading(true);
      setError("");

      try {
        const params = { lang, userID: uid };
        if (selectedA) params.sortA = selectedA;
        if (selectedB) params.sortB = selectedB;
        if (selectedC) params.sortC = selectedC;

        const data = await apiGetVariantsByParent(id, params);
        const arr = Array.isArray(data) ? data : [];
        const norm = arr.map(normalizeItem);

        if (cancelled) return;

        if (norm.length === 0) {
          setSelectedA("");
          setSelectedB("");
          setSelectedC("");

          const full = await apiGetVariantsByParent(id, { lang, userID: uid });
          const fullNorm = (Array.isArray(full) ? full : []).map(normalizeItem);

          if (!cancelled) {
            setVariants(fullNorm);
            setAllVariants(fullNorm);

            const key = `${id}::${variantFromQuery || ""}`;
            if (variantFromQuery && appliedVariantKeyRef.current !== key) {
              const pick = fullNorm.find(
                (v) => String(v.id) === String(variantFromQuery),
              );
              if (pick) {
                appliedVariantKeyRef.current = key;
                setSelectedVariantId(pick.id);
                setProduct(pick);
                setLoading(false);
                return;
              }
              appliedVariantKeyRef.current = key;
            }

            setProduct(fullNorm[0] || null);
            setSelectedVariantId(fullNorm[0]?.id || null);
          }
          return;
        }

        setVariants(norm);
        if (!selectedA && !selectedB && !selectedC) setAllVariants(norm);

        const key = `${id}::${variantFromQuery || ""}`;
        if (variantFromQuery && appliedVariantKeyRef.current !== key) {
          const pick = norm.find(
            (v) => String(v.id) === String(variantFromQuery),
          );
          if (pick) {
            appliedVariantKeyRef.current = key;
            setSelectedVariantId(pick.id);
            setProduct(pick);
            return;
          }
          appliedVariantKeyRef.current = key;
        }

        let next = selectedVariantId
          ? norm.find((v) => String(v.id) === String(selectedVariantId))
          : null;
        if (!next) next = norm[0] || null;

        setSelectedVariantId(next?.id || null);
        setProduct(next || null);
      } catch (e) {
        console.error("Load variants failed:", e);
        if (!cancelled) {
          setError(lang === "ZH" ? "無法載入商品" : "Failed to load product");
          setVariants([]);
          if (!selectedA && !selectedB && !selectedC) setAllVariants([]);
          setProduct(null);
          setSelectedVariantId(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => (cancelled = true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lang, uid, selectedA, selectedB, selectedC, variantFromQuery]);

  const variantCards = useMemo(
    () =>
      variants.map((v) => ({
        id: v.id,
        name:
          v.variantName ||
          v?.main?.variantName ||
          v?.main?.["產品變體名稱"] ||
          v?.productName,
        image: v.image,

        // ✅ IMPORTANT: keep favourite for the scroller cards
        favourite: v?.favourite ?? 0,
      })),
    [variants],
  );

  const handleVariantChange = (vid) => {
    setSelectedVariantId(vid);
    const v = variants.find((x) => String(x.id) === String(vid));
    if (v) setProduct(v);
  };

  const productCrumbs = useMemo(() => {
    const base =
      Array.isArray(receivedCrumbs) && receivedCrumbs.length > 0
        ? receivedCrumbs
        : [
            {
              label: lang === "ZH" ? "商品資訊" : "Products",
              href: "/商品資訊",
            },
          ];

    return [
      ...base,
      {
        label:
          (product && product.productName) ||
          id ||
          (lang === "ZH" ? "商品明細" : "Product Detail"),
        href: null,
      },
    ];
  }, [receivedCrumbs, product, id, lang]);

  const handleCrumbClick = (crumb) => {
    if (!crumb?.href) return;

    const rootNorm = "商品資訊";
    const rootPath = "/商品資訊";
    const hrefNorm = crumb.href;

    const isRootTarget = !hrefNorm || hrefNorm === rootNorm;
    const search = isRootTarget ? "" : `?cat=${encodeURIComponent(hrefNorm)}`;

    navigate(`${rootPath}${search}`, {
      state: {
        fromBreadcrumbs: receivedCrumbs,
        fromCategoryHref,
      },
    });
  };

  if (loading && !product) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return <NotFoundPage />;
  }

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 3, md: 5 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            mb: 3,
            overflowX: "auto",
            maxWidth: "100%",
            whiteSpace: "nowrap",
            "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
          }}
        >
          <Breadcrumbs aria-label="breadcrumb">
            {productCrumbs.map((c, idx) => {
              const isLast = idx === productCrumbs.length - 1;
              if (isLast || !c.href) {
                return (
                  <Typography key={`${c.label}-${idx}`} color="text.primary">
                    {c.label}
                  </Typography>
                );
              }

              return (
                <MLink
                  key={`${c.href}-${idx}`}
                  underline="hover"
                  color="inherit"
                  component="button"
                  onClick={() => handleCrumbClick(c)}
                >
                  {c.label}
                </MLink>
              );
            })}
          </Breadcrumbs>
        </Box>

        <ProductDetailB2BTop
          product={product}
          override={{ name: product?.productName }}
          variantOptions={variantOptions}
          selectedA={selectedA}
          selectedB={selectedB}
          selectedC={selectedC}
          onSelectA={setSelectedA}
          onSelectB={setSelectedB}
          onSelectC={setSelectedC}
          variantsAll={allVariants}
          lang={lang}
        />

        <Divider sx={{ my: 4 }} />

        <ProductDetailB2BVariants
          variants={variantCards}
          value={selectedVariantId}
          onChange={handleVariantChange}
          selectedA={selectedA}
          selectedB={selectedB}
          selectedC={selectedC}
          onClearA={() => setSelectedA("")}
          onClearB={() => setSelectedB("")}
          onClearC={() => setSelectedC("")}
          lang={lang}
          userID={uid} // ✅ IMPORTANT: allow ProductCard to call favourite API and show state
        />

        <Divider sx={{ my: 4 }} />

        <ProductDetailB2BInfo
          productId={product?.id}
          isLoggedIn={isLoggedIn}
          pdfUrl="/files/Test.pdf"
          product={product}
          lang={lang}
        />

        <ProductSuggestionSection productCode={id} lang={lang} userID={uid} />
      </Container>
    </Box>
  );
}
